import useClickOutside from "hooks/useClickOutside";
import useFieldMaps from "hooks/useFieldMaps";
import React from "react";
import { TreePicker } from "rsuite";
import _ from "underscore";
import Constants from "utils/Constants";

const ActionDescriptions = Constants.ACTION_DESCRIPTIONS;
const empty = [];

/**
 * ActionsPicker - RSUITE Tree picker for advanced action selection.
 * @param {Array} availableActions - The array of available actions to use in this picker.
 * @param {Array<string>} availableActionTypes - The acceptable types of actions (used to filter availableActions)
 * @param {Array} extraOptions - Array of extra options to place above action options.
 * @param {function} onChange - Callback function for when TreePicker value changes.
 * @param {string} value - The current value of this picker.
 * @param {boolean} useRecordCreateUpdateOptions - Whether or not to use Field mappings in record-create-update actions
 * @param {string} fieldType - FieldType of field the action is being selected for, used with useRecordCreatEUpdateOptions is true,
 * @param {object} fieldMap - FieldMap to be used when useRecordCreateUpdateOptions is true.
 */
const ActionsPicker = ({
  availableActions,
  availableActionTypes = empty,
  extraOptions = empty,
  onChange,
  value,
  placeholder = "Select Action",
  width = "100%",
  useRecordCreateUpdateOptions,
  fieldType,
}) => {
  const [open, setOpen] = React.useState(false);
  const { getFieldMap } = useFieldMaps({ value: null });

  const TreePickerData = React.useMemo(() => {
    const data = [...extraOptions];

    for (const action of availableActions) {
      if (!availableActionTypes.includes(action.Type)) continue;

      const foundItemParent = _.findWhere(data, {
        value: `_unselectable-${action.Type}`,
      });

      const newItem = {
        label: `Action ${action.Index}`,
        value: action.id,
        labelWithDescription: `Action ${action.Index} - ${action.Name}`,
        isAction: true,
      };

      if (useRecordCreateUpdateOptions) {
        const actionChildren = [];

        for (const mapping of action.FieldMapping) {
          const fieldMap = getFieldMap(action?.ModuleAPIName);
          const field = fieldMap?.[mapping?.Field];

          if (field?.FieldType === fieldType) {
            actionChildren.push({
              label: `Actions ${action.Index}'s ${field?.Name}`,
              value: `${action.id}.${mapping?.Field}`,
              labelWithDescription: `Actions ${action.Index}'s "${field?.Name}" Result`,
              isAction: true,
            });
          }
        }

        if (actionChildren.length > 0) {
          newItem.children = actionChildren;
        }
      }

      if (foundItemParent) {
        foundItemParent.children.push(newItem);
      } else {
        data.push({
          label: ActionDescriptions[action.Type],
          children: [newItem],
          value: `_unselectable-${action.Type}`,
        });
      }
    }

    return data;
  }, [
    availableActions,
    availableActionTypes,
    extraOptions,
    useRecordCreateUpdateOptions,
    fieldType,
    getFieldMap,
  ]);

  return (
    <TreePicker
      open={open}
      data={TreePickerData}
      placeholder={placeholder}
      placement="bottomEnd"
      size="sm"
      style={{ width }}
      menuStyle={{ width: 420 }}
      defaultExpandAll
      preventOverflow
      renderTreeNode={MenuItem}
      onSelect={handleSelect}
      onClick={() => setOpen((prev) => !prev)}
      value={value}
      searchable={false}
      renderMenu={RenderMenu}
    />
  );

  function handleChange(newValue) {
    onChange(newValue);
    setOpen(false);
  }

  function handleSelect(selectedItem) {
    // _unselectable is the value for groups/items that aren't meant for selection.
    if (selectedItem.value.startsWith("_unselectable")) {
      return;
    }

    // Handle full action selection.
    if (selectedItem.isAction) {
      handleChange({ ActionID: selectedItem.value });
      return;
    }

    handleChange(selectedItem.value);
  }

  function MenuItem(item) {
    const disabled = item.value.startsWith("_unselectable");
    const text = item?.labelWithDescription ?? item?.label;

    return (
      <div
        className="d-flex align-items-center"
        style={{ cursor: disabled ? "not-allowed" : "pointer" }}
      >
        {item.icon && (
          <i className={item.icon} style={{ fontSize: 20, paddingRight: 6 }} />
        )}
        {text}
      </div>
    );
  }

  function RenderMenu(menu) {
    const menuRef = React.useRef();
    useClickOutside(menuRef, () => setOpen(false));

    return (
      <div ref={menuRef}>
        <div
          className="pl-4 py-2 border-bottom font-weight-500 text-dark mb-2"
          style={{ fontSize: 14 }}
        >
          {placeholder}
        </div>
        {menu}
      </div>
    );
  }
};

export default ActionsPicker;
